import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { UNSAFE_DataRouterContext, useBlocker, useLocation } from "react-router-dom";
import { Alert, AlertIcon, Button, ChakraProvider, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from "@chakra-ui/react";

import { VideoDraftContext } from "../../contexts/VideoDraftContext";
import theme from "../layout/theme";
const StandaloneContext = createContext(null);

// Only the coordinator registers with the router, not each form. Standalone
// player previews still get editor-close and browser-unload protection.
const DraftRouteBlocker = ({ hasChanges, requestExit }) => {
  const blocker = useBlocker(hasChanges);
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    requestExit(blocker.proceed, null, blocker.reset);
  }, [blocker, requestExit]);
  return null;
};

const DraftRouteProtection = (props) => {
  const location = useLocation();
  // Case Prep has its own autosave blocker; only one blocker owns each route.
  return location.pathname.startsWith("/video/") ? <DraftRouteBlocker {...props} /> : null;
};

export const VideoDraftProvider = ({ children }) => {
  const router = useContext(UNSAFE_DataRouterContext || StandaloneContext);
  const drafts = useRef(new Map());
  const pending = useRef(null);
  const active = useRef(true);
  const savingRef = useRef(false);
  const approvedExit = useRef(false);
  const stayRef = useRef(null);
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const dirtyDrafts = useCallback((ids) => Array.from(drafts.current.entries())
    .filter(([id, getDraft]) => (!ids || ids.includes(id)) && getDraft().dirty)
    .map(([, getDraft]) => getDraft()), []);
  const hasChanges = useCallback(() => !approvedExit.current && dirtyDrafts().length > 0, [dirtyDrafts]);
  const notify = useCallback(() => {
    if (!pending.current) return;
    const changes = dirtyDrafts(pending.current.ids);
    const next = {
      names: changes.map((draft) => draft.name),
      blockedReason: changes.find((draft) => draft.saveBlockedReason)?.saveBlockedReason,
      pendingSave: changes.some((draft) => draft.pending),
    };
    setDialog((previous) => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
  }, [dirtyDrafts]);
  const register = useCallback((id, getDraft) => {
    drafts.current.set(id, getDraft);
    return () => {
      if (drafts.current.get(id) === getDraft) drafts.current.delete(id);
    };
  }, []);
  const requestExit = useCallback((proceed, ids = null, cancel) => {
    if (pending.current) return;
    const changes = dirtyDrafts(ids);
    if (!changes.length) { proceed(); return; }
    pending.current = { proceed, cancel, ids };
    setError(null);
    notify();
  }, [dirtyDrafts, notify]);

  useEffect(() => {
    active.current = true;
    const warn = (event) => {
      if (!hasChanges()) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => {
      active.current = false;
      pending.current = null;
      window.removeEventListener("beforeunload", warn);
    };
  }, [hasChanges]);

  const stay = () => {
    if (savingRef.current) return;
    const exit = pending.current;
    pending.current = null;
    setDialog(null);
    setError(null);
    exit?.cancel?.();
  };
  const finish = (exit) => {
    if (!active.current || pending.current !== exit) return;
    pending.current = null;
    setDialog(null);
    // Explicit Sign out can navigate before React commits the saved/discarded
    // form state. The user's all-editor decision also covers that navigation.
    approvedExit.current = exit.ids === null;
    Promise.resolve(exit.proceed()).finally(() => { approvedExit.current = false; });
  };
  const discard = () => {
    if (savingRef.current) return;
    const exit = pending.current;
    if (!exit) return;
    const changes = dirtyDrafts(exit.ids);
    if (changes.some((draft) => draft.pending)) return;
    changes.forEach((draft) => draft.discard());
    finish(exit);
  };
  const save = async () => {
    if (savingRef.current) return;
    const exit = pending.current;
    if (!exit) return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      for (const draft of dirtyDrafts(exit.ids)) {
        if (!active.current || pending.current !== exit) return;
        if (draft.saveBlockedReason) throw new Error(draft.saveBlockedReason);
        if (await draft.save() !== true) throw new Error("Your changes could not be saved. They are still open on this page. Try again or stay to review them.");
      }
      finish(exit);
    } catch (failure) {
      if (active.current) setError(failure.message);
    } finally {
      savingRef.current = false;
      if (active.current) setSaving(false);
    }
  };
  const value = useMemo(() => ({ register, requestExit, notify }), [register, requestExit, notify]);

  return <VideoDraftContext.Provider value={value}>
    {router && <DraftRouteProtection hasChanges={hasChanges} requestExit={requestExit} />}
    {children}
    {/* The coordinator sits above the routed page's Layout/ChakraProvider. */}
    <ChakraProvider theme={theme} resetCSS={false}>
    <Modal isOpen={Boolean(dialog)} onClose={stay} initialFocusRef={stayRef} closeOnOverlayClick={false} closeOnEsc={!saving} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Save your changes before leaving?</ModalHeader>
        <ModalBody>
          <Text>{dialog?.names.length ? `You have unsaved changes to ${dialog.names.join(", ")}.` : "Your changes have been saved."}</Text>
          {dialog?.blockedReason && <Text mt={3}>{dialog.blockedReason}</Text>}
          {error && <Alert status="error" mt={3}><AlertIcon />{error}</Alert>}
        </ModalBody>
        <ModalFooter gap={3} flexWrap="wrap">
          <Button ref={stayRef} onClick={stay} isDisabled={saving}>Stay</Button>
          <Button variant="outline" onClick={discard} isDisabled={saving || dialog?.pendingSave}>Discard changes</Button>
          <Button colorScheme="blue" onClick={save} isLoading={saving} isDisabled={Boolean(dialog?.blockedReason)}>Save and leave</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    </ChakraProvider>
  </VideoDraftContext.Provider>;
};
